import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Link as LinkIcon, Unlink, ShieldCheck, AlertTriangle, MessageCircle,
} from "lucide-react";
import { TIERS } from "@/lib/tiers";

/**
 * Dashboard widget that lets a user link / re-sync / unlink their Discord
 * account. The website role is the source of truth; this card simply mirrors
 * the latest sync status returned by the backend.
 */
export default function DiscordConnect() {
  const [status, setStatus] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/discord/status");
      setStatus(data);
    } catch (e) {
      console.error("Failed to load Discord status", e);
      setStatus({ connected: false, oauth_configured: false, bot_configured: false });
    }
  };

  useEffect(() => { load(); }, []);

  // Surface OAuth callback result (?discord_status=connected | error&message=...)
  // Read query string directly so we don't keep a stateful copy of search params.
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const s = search.get("discord_status");
    if (!s) return;
    const msg = search.get("message");
    if (s === "connected") {
      toast.success("Discord connected — roles are being synced.");
      load();
    } else if (s === "error") {
      toast.error("Discord connection failed: " + (msg || "Unknown error"));
    }
    search.delete("discord_status");
    search.delete("message");
    const newQs = search.toString();
    const newUrl = window.location.pathname + (newQs ? "?" + newQs : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/discord/oauth/start");
      if (data.authorize_url) window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/discord/resync");
      if (data.ok) toast.success("Discord roles re-synced.");
      else toast.error("Sync had issues: " + (data.discord_sync_detail || "see logs"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Discord? Your synced membership roles on the server will be removed.")) return;
    setBusy(true);
    try {
      await api.post("/discord/disconnect");
      toast.success("Discord disconnected.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="p-6 rounded-xl border border-white/10 bg-zinc-950 flex items-center gap-3 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Discord status…
      </div>
    );
  }

  const tier = TIERS[status.website_role] || TIERS.member;

  // ============ Not connected ============
  if (!status.connected) {
    return (
      <div
        className="p-6 rounded-xl border border-indigo-500/40 bg-gradient-to-br from-indigo-950/40 via-zinc-950 to-zinc-950"
        data-testid="discord-card-disconnected"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-indigo-300">
              <MessageCircle className="w-5 h-5" />
              <span className="font-display text-lg font-bold tracking-tight">Discord</span>
              <Badge className="bg-indigo-950/60 text-indigo-200 border border-indigo-500/40">Not connected</Badge>
            </div>
            <p className="mt-2 text-zinc-300 text-sm max-w-xl">
              Connect Discord to receive membership roles, access private channels, and submit feedback.
            </p>
            {!status.oauth_configured && (
              <div className="mt-3 flex items-center gap-2 text-amber-300 text-xs">
                <AlertTriangle className="w-4 h-4" />
                Discord OAuth is not configured yet — please contact the administrator.
              </div>
            )}
          </div>
          <Button
            onClick={connect}
            disabled={busy || !status.oauth_configured}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="discord-connect-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
            Connect Discord
          </Button>
        </div>
      </div>
    );
  }

  // ============ Connected ============
  const syncBadge = (() => {
    const s = status.discord_sync_status;
    if (s === "synced") return <Badge className="bg-emerald-900/40 text-emerald-300 border border-emerald-500/30">Synced</Badge>;
    if (s === "pending") return <Badge className="bg-amber-900/40 text-amber-300 border border-amber-500/30">Pending</Badge>;
    if (s === "error") return <Badge className="bg-rose-900/40 text-rose-300 border border-rose-500/30">Error</Badge>;
    if (s === "not_in_guild") return <Badge className="bg-amber-900/40 text-amber-300 border border-amber-500/30">Not in Server</Badge>;
    if (s === "skipped") return <Badge className="bg-zinc-800 text-zinc-300 border border-white/10">Skipped</Badge>;
    return <Badge className="bg-zinc-800 text-zinc-300 border border-white/10">Unknown</Badge>;
  })();

  return (
    <div
      className="p-6 rounded-xl border border-indigo-500/40 bg-gradient-to-br from-indigo-950/40 via-zinc-950 to-zinc-950"
      data-testid="discord-card-connected"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-3 min-w-0">
          <div className="flex items-center gap-2 text-indigo-300">
            <MessageCircle className="w-5 h-5" />
            <span className="font-display text-lg font-bold tracking-tight">Discord</span>
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-sm">
            <div className="text-zinc-400 text-xs uppercase tracking-wider">Discord Username</div>
            <div className="text-white font-semibold mt-0.5 break-all" data-testid="discord-username">
              {status.discord_username || "—"}
            </div>
          </div>
          <div className="text-sm">
            <div className="text-zinc-400 text-xs uppercase tracking-wider">Synced Role</div>
            <div className={`font-display text-base font-bold mt-0.5 ${tier.color}`} data-testid="discord-synced-role">
              {tier.label}
            </div>
            <div className="text-zinc-500 text-xs mt-1">
              Discord mirrors your website role. Only the three premium membership roles (Elite / Hardcore / God Tier) are managed — Admin, Mod, Server Booster, color, and manually-assigned roles are never modified.
            </div>
          </div>
          <div className="text-sm flex items-center gap-2">
            <span className="text-zinc-400 text-xs uppercase tracking-wider">Sync Status</span>
            <span data-testid="discord-sync-status">{syncBadge}</span>
            {status.discord_synced_at && (
              <span className="text-zinc-500 text-xs">
                · {new Date(status.discord_synced_at).toLocaleString()}
              </span>
            )}
          </div>
          {status.discord_sync_detail && status.discord_sync_status !== "synced" && (
            <div className="text-xs text-zinc-500 break-words">
              {status.discord_sync_detail}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          <Button
            onClick={resync}
            disabled={busy}
            variant="outline"
            className="border-indigo-500/40 text-indigo-200 hover:bg-indigo-950/40"
            data-testid="discord-resync-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Resync
          </Button>
          <Button
            onClick={disconnect}
            disabled={busy}
            variant="outline"
            className="border-rose-500/30 text-rose-300 hover:bg-rose-950/30"
            data-testid="discord-disconnect-btn"
          >
            <Unlink className="w-4 h-4 mr-2" />
            Disconnect
          </Button>
        </div>
      </div>
    </div>
  );
}
